import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { maintenanceService, MaintenanceLog, CreateMaintenanceData, MaintenanceFilters, RaiseMaintenanceTicketData, MaintenanceMetrics, MaintenanceSchedule, ScheduleMaintenanceData } from '../../services/maintenanceService';

interface MaintenanceState {
  logs: MaintenanceLog[];
  currentLog: MaintenanceLog | null;
  schedules: MaintenanceSchedule[];
  metrics: MaintenanceMetrics | null;
  isLoading: boolean;
  isDetailLoading: boolean;
  isScheduleLoading: boolean;
  isDashboardLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  scheduleTotalCount: number;
  scheduleCurrentPage: number;
  scheduleTotalPages: number;
}

const initialState: MaintenanceState = {
  logs: [],
  currentLog: null,
  schedules: [],
  metrics: null,
  isLoading: false,
  isDetailLoading: false,
  isScheduleLoading: false,
  isDashboardLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  scheduleTotalCount: 0,
  scheduleCurrentPage: 1,
  scheduleTotalPages: 1
};

// Async thunks
export const fetchMaintenanceLogs = createAsyncThunk(
  'maintenance/fetchLogs',
  async (
    filters: MaintenanceFilters | undefined,
    thunkAPI
  ) => {
    try {
      const response = await maintenanceService.getMaintenanceLogs(filters);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to fetch maintenance logs'
      );
    }
  }
);

export const fetchMaintenanceLog = createAsyncThunk(
  'maintenance/fetchLog',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceLog(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance log');
    }
  }
);

export const createMaintenanceLog = createAsyncThunk(
  'maintenance/createLog',
  async (data: CreateMaintenanceData, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.createMaintenanceLog(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create maintenance log');
    }
  }
);
export const raiseMaintenanceTicket = createAsyncThunk(
  'maintenance/raiseTicket',
  async (data: RaiseMaintenanceTicketData, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.raiseMaintenanceTicket(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to raise maintenance ticket');
    }
  }
);

export const deleteMaintenanceLog = createAsyncThunk(
  'maintenance/deleteLog',
  async (id: string, { rejectWithValue }) => {
    try {
      await maintenanceService.deleteMaintenanceLog(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete maintenance log');
    }
  }
);

export const fetchMaintenanceDashboardData = createAsyncThunk(
  'maintenance/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceDashboardData();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance dashboard data');
    }
  }
);

export const fetchMaintenanceSchedules = createAsyncThunk(
  'maintenance/fetchSchedules',
  async (filters: MaintenanceFilters, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceSchedules(filters);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance schedules');
    }
  }
);

export const fetchOpenTickets = createAsyncThunk(
  'maintenance/fetchOpenTickets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getOpenTickets();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch open tickets');
    }
  }
);

export const fetchLogsByStatus = createAsyncThunk(
  'maintenance/fetchLogsByStatus',
  async (status: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getLogsByStatus(status);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || `Failed to fetch logs for status: ${status}`);
    }
  }
);

export const updateMaintenanceLog = createAsyncThunk(
  'maintenance/updateLog',
  async ({ id, updateData }: { id: string; updateData: Partial<MaintenanceLog> }, { rejectWithValue }) => {
    try {
      // Uses the same endpoint as startCorrectiveMaintenance
      const response = await maintenanceService.startCorrectiveMaintenance(id, updateData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update maintenance log');
    }
  }
);

export const createSchedule = createAsyncThunk(
  'maintenance/createSchedule',
  async (data: ScheduleMaintenanceData, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.createSchedule(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create schedule');
    }
  }
);

export const updateSchedule = createAsyncThunk(
  'maintenance/updateSchedule',
  async ({ id, data }: { id: string; data: Omit<ScheduleMaintenanceData, 'asset'> }, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.updateSchedule(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update schedule');
    }
  }
);

export const deleteSchedule = createAsyncThunk(
  'maintenance/deleteSchedule',
  async (id: string, { rejectWithValue }) => {
    try {
      await maintenanceService.deleteSchedule(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete schedule');
    }
  }
);

export const completeMaintenanceLog = createAsyncThunk(
  'maintenance/completeLog',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.completeCorrectiveMaintenance(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete maintenance log');
    }
  }
);

const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentLog: (state) => {
      state.currentLog = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH operations - ONLY these should set the global error state
      
      // Fetch maintenance logs
      .addCase(fetchMaintenanceLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (action.payload && typeof action.payload === 'object' && 'result' in action.payload) {
          state.logs = action.payload.result;
          state.totalCount = action.payload.totalCount || 0;
          state.currentPage = action.payload.currentPage || 1;
          state.totalPages = action.payload.totalPages || 1;
        } else {
          state.logs = action.payload;
        }
      })
      .addCase(fetchMaintenanceLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      
      // Fetch single maintenance log
      .addCase(fetchMaintenanceLog.pending, (state) => {
        state.isDetailLoading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceLog.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.currentLog = action.payload.data || action.payload;
      })
      .addCase(fetchMaintenanceLog.rejected, (state, action) => {
        state.isDetailLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      
      // Fetch maintenance dashboard data
      .addCase(fetchMaintenanceDashboardData.pending, (state) => {
        state.isDashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceDashboardData.fulfilled, (state, action) => {
        state.isDashboardLoading = false;
        state.error = null;
        const payload = action.payload as any;
        const dataWrapper = payload?.data ?? payload;
        const logs = dataWrapper?.logs;

        // Handle nested { metrics: {...} } shape OR flat root-level metrics shape
        const metrics = dataWrapper?.metrics ?? (
          'openMaintenanceCount' in dataWrapper
            ? {
                openMaintenanceCount: dataWrapper.openMaintenanceCount,
                completedMaintenanceCount: dataWrapper.completedMaintenanceCount,
                pendingMaintenanceCount: dataWrapper.pendingMaintenanceCount,
                inProgressMaintenanceCount: dataWrapper.inProgressMaintenanceCount,
                scheduledMaintenanceCount: dataWrapper.scheduledMaintenanceCount,
                overdueMaintenanceCount: dataWrapper.overdueMaintenanceCount,
                operationalAssetsPercentage: dataWrapper.operationalAssetsPercentage,
              }
            : null
        );

        if (Array.isArray(logs)) {
          state.logs = logs;
        } else if (logs) {
          state.logs = logs?.result ?? state.logs;
          state.totalCount = logs?.totalCount ?? state.totalCount;
          state.currentPage = logs?.currentPage ?? state.currentPage;
          state.totalPages = logs?.totalPages ?? state.totalPages;
        }
        if (metrics) state.metrics = metrics;
      })
      .addCase(fetchMaintenanceDashboardData.rejected, (state, action) => {
        state.isDashboardLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      
       // Fetch maintenance schedules
      .addCase(fetchMaintenanceSchedules.pending, (state) => {
        state.isScheduleLoading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceSchedules.fulfilled, (state, action) => {
        state.isScheduleLoading = false;
        state.error = null; // Clear any previous fetch errors
        const data = action.payload;
        state.schedules = data.result;
        state.scheduleTotalCount = data.totalCount;
        state.scheduleCurrentPage = data.currentPage;
        state.scheduleTotalPages = data.totalPages;
      })
      .addCase(fetchMaintenanceSchedules.rejected, (state, action) => {
        state.isScheduleLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // CREATE/UPDATE/DELETE operations - these should NOT set the global error state
      
      // Create maintenance log
      .addCase(createMaintenanceLog.pending, (state) => {
        // Don't clear the error state here - keep fetch errors visible
      })
      .addCase(createMaintenanceLog.fulfilled, (state, action) => {
        state.logs.unshift(action.payload);
        state.totalCount += 1;
        // Don't modify error state
      })
      .addCase(createMaintenanceLog.rejected, (state, action) => {
        // Don't set error state - let the modal handle create errors
      })
      
      // Raise maintenance ticket
      .addCase(raiseMaintenanceTicket.pending, (state) => {
        // Don't clear the error state here
      })
      .addCase(raiseMaintenanceTicket.fulfilled, (state, action) => {
        state.logs.unshift(action.payload);
        state.totalCount += 1;
        // Don't modify error state
      })
      .addCase(raiseMaintenanceTicket.rejected, (state, action) => {
        // Don't set error state - let the modal handle ticket errors
      })
      
      // Delete maintenance log - these should NOT set the global error state
      .addCase(deleteMaintenanceLog.pending, (state) => {
        // Don't clear the error state here
      })
      .addCase(deleteMaintenanceLog.fulfilled, (state, action) => {
        state.logs = state.logs.filter(log => log._id !== action.payload);
        state.totalCount -= 1;
        // Don't modify error state
      })
      .addCase(deleteMaintenanceLog.rejected, (state, action) => {
        // Don't set error state - let the modal/confirmation handle delete errors
      })
      
      // Create schedule
      .addCase(createSchedule.pending, (state) => {
        // Don't clear the error state here
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        state.schedules.unshift(action.payload);
        state.scheduleTotalCount += 1;
        // Don't modify error state
      })
      .addCase(createSchedule.rejected, (state, action) => {
        // Don't set error state - let the modal handle create errors
      })
      
      // Update schedule
      .addCase(updateSchedule.pending, (state) => {
        // Don't clear the error state here
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        const index = state.schedules.findIndex(schedule => schedule._id === action.payload._id);
        if (index !== -1) {
          state.schedules[index] = action.payload;
        }
        // Don't modify error state
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        // Don't set error state - let the modal handle update errors
      })
      
      // Delete schedule
      .addCase(deleteSchedule.pending, (state) => {
        // Don't clear the error state here
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        state.schedules = state.schedules.filter(schedule => schedule._id !== action.payload);
        state.scheduleTotalCount -= 1;
        // Don't modify error state
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        // Don't set error state - let the modal/confirmation handle delete errors
      })

      // Complete maintenance log
      .addCase(completeMaintenanceLog.pending, (state) => {
        // Don't clear the error state here
      })
      .addCase(completeMaintenanceLog.fulfilled, (state, action) => {
        const payload = action.payload as any;
        const updatedLog = payload?.data ?? payload;
        if (updatedLog?._id) {
          const index = state.logs.findIndex(log => log._id === updatedLog._id);
          if (index !== -1) {
            state.logs[index] = { ...state.logs[index], ...updatedLog };
          }
        }
        // Don't modify error state
      })
      .addCase(completeMaintenanceLog.rejected, (state, action) => {
        // Don't set error state - let the modal handle complete errors
      });
  }
});

export const { clearError, clearCurrentLog } = maintenanceSlice.actions;
export default maintenanceSlice.reducer;